var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
/* eslint-disable react/require-default-props */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import ConfirmDialog from '@components/ConfirmDialog/ConfirmDialog';
import LetterAvatar from '@components/Avatar/LetterAvatar';
import { Button } from '@ui';
import ImageAvatar from '@components/Avatar/ImageAvatar';
import TooltipText from '@components/v2/TooltipText';
import UiSizes from '@enums/UiSizes';
import { translate } from '@utils/i18n';
import { workOrderPath, inspectionPath } from '../../../routes';
import VisibilityToggle from './VisibilityToggle';
import useNotesMutations from '../hooks/useNotesMutations';
import NoteActions from './NoteActions';
import dateFormatter from '../utils/dateFormatter';
const namespace = 'features.activity_feed.note';
export default function Note({ note, isBuilding, isNew }) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
    const [edit, setEdit] = useState(false);
    const [notes, setNotes] = useState(note.notes);
    const [removeModal, setRemoveModal] = useState(false);
    const noteRef = useRef(null);
    const { updateNote, deleteNote } = useNotesMutations({
        buildingId: note.buildingId,
        inspectionId: note.inspectionId,
        workOrderId: note.workOrderId
    });
    useEffect(() => {
        if (edit && noteRef.current) {
            noteRef.current.style.height = `${noteRef.current.scrollHeight}px`;
        }
    }, [edit]);
    const onRequestEdit = useCallback(() => {
        setEdit(true);
    }, []);
    const onRequestRemove = useCallback(() => {
        setRemoveModal(true);
    }, []);
    const onUpdate = (e) => __awaiter(this, void 0, void 0, function* () {
        e.preventDefault();
        try {
            yield updateNote({ id: note.id, note: { notes, visibility: note.visibility } });
        }
        catch (error) {
            toast.error('There was an error updating the note');
        }
        setEdit(false);
    });
    const onRemove = () => __awaiter(this, void 0, void 0, function* () {
        try {
            yield deleteNote({ id: note.id });
        }
        catch (error) {
            toast.error('There was an error deleting the note');
        }
        setRemoveModal(false);
    });
    const avatar = ((_a = note.technician) === null || _a === void 0 ? void 0 : _a.photoUrl) || ((_b = note.user) === null || _b === void 0 ? void 0 : _b.avatar);
    return (React.createElement("div", { className: `activity__item--note ${isNew ? '--new' : ''} noteContainer` },
        React.createElement("div", { className: "noteAvatarContainer" },
            avatar && (React.createElement(ImageAvatar, { src: avatar, borderColor: ((_c = note.technician) === null || _c === void 0 ? void 0 : _c.color) || '#bbb', size: UiSizes.mediumLarge })),
            ((_d = note.user) === null || _d === void 0 ? void 0 : _d.name) && !note.technician && !((_e = note.user) === null || _e === void 0 ? void 0 : _e.avatar) && (React.createElement(LetterAvatar, { letter: (_f = note.user) === null || _f === void 0 ? void 0 : _f.name[0], backgroundColor: "#bbb", size: UiSizes.mediumLarge }))),
        React.createElement("div", { className: "activity__body noteInfoContainer" },
            React.createElement("span", { className: "authorText" }, (_h = (_g = note.technician) === null || _g === void 0 ? void 0 : _g.name) !== null && _h !== void 0 ? _h : (_j = note.user) === null || _j === void 0 ? void 0 : _j.name),
            !note.inspectionId && note.woNumber && isBuilding && (React.createElement("div", { className: "activity__ref" },
                React.createElement("span", null, translate('related', { namespace })),
                note.disabledWo ? (React.createElement("span", { className: "qmb-link disabled" },
                    translate('work_order', { namespace }),
                    " #",
                    note.woNumber)) : (React.createElement("a", { href: workOrderPath(note.woNumber) },
                    translate('work_order', { namespace }),
                    " #",
                    note.woNumber)))),
            note.inspectionId && isBuilding && (React.createElement("div", { className: "activity__ref" },
                React.createElement("span", null, translate('related', { namespace })),
                React.createElement("a", { href: inspectionPath(note.inspectionId) },
                    translate('inspection', { namespace }),
                    " #",
                    note.inspectionId))),
            !edit && (React.createElement("span", { className: "noteText", style: { whiteSpace: 'pre-line' } }, note.notes)),
            edit && (React.createElement("div", { className: "qmb-textarea--note--blank--x-full" },
                React.createElement("textarea", { ref: noteRef, value: notes, onChange: (e) => setNotes(e.target.value), name: "notes" }),
                React.createElement("div", { className: "textarea__controls" },
                    React.createElement(Button, { type: "button", onClick: () => {
                            setNotes(note.notes);
                            setEdit(false);
                        } }, translate('cancel', { namespace: 'features.commons' })),
                    React.createElement(Button, { disabled: notes.length === 0, variant: "highlighted", onClick: (e) => {
                            void onUpdate(e);
                        } }, translate('save', { namespace: 'features.commons' }))),
                React.createElement("svg", { viewBox: "0 0 32 32", className: "textarea__accent" },
                    React.createElement("path", { d: "M 32 0 L 0 32", className: "accent__diag" }),
                    React.createElement("path", { d: "M 26 0.5 L 0 0.5", className: "accent__x" }),
                    React.createElement("path", { d: "M 0.5 26 L 0.5 0", className: "accent__y" }))))),
        React.createElement("div", { className: "noteActionsContainer" },
            !edit && (React.createElement(NoteActions, { canEdit: !!((_k = note.user) === null || _k === void 0 ? void 0 : _k.isOwner), canDelete: note.canEditVisibilityDestroy, onEdit: onRequestEdit, onDelete: onRequestRemove })),
            React.createElement(VisibilityToggle, { note: note }),
            React.createElement(TooltipText, { text: new Date(note.datetime).toLocaleString() },
                React.createElement("span", null, dateFormatter(new Date(note.datetime))))),
        removeModal && (React.createElement(ConfirmDialog, { title: translate('modal_title', { namespace }), description: translate('confirm_delete', { namespace }), onConfirm: () => {
                void onRemove();
            }, onCancel: () => setRemoveModal(false), yesLabel: translate('delete', { namespace: 'features.commons' }) }))));
}
